const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Get authentication token from localStorage
 */
const getToken = () => {
    return localStorage.getItem('authToken');
};

/**
 * Set authentication token in localStorage
 */
const setToken = (token) => {
    localStorage.setItem('authToken', token);
};

/**
 * Remove authentication token from localStorage
 */
const removeToken = () => {
    localStorage.removeItem('authToken');
};

/**
 * Make authenticated API request
 */
const apiRequest = async (endpoint, options = {}) => {
    const token = getToken();

    const config = {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    };

    // Add authorization header if token exists
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${API_URL}${endpoint}`, config);
        const data = await response.json();

        // Handle authentication errors
        if (!response.ok) {
            // Only redirect to login if 401 (Unauthorized) and NOT on login endpoint
            if (response.status === 401 && !endpoint.includes('/auth/login')) {
                removeToken();
                window.location.href = '/';
            }
            throw new Error(data.message || 'API request failed');
        }

        return data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

/**
 * Authentication API
 */
export const authAPI = {
    /**
     * Register new user
     */
    register: async (userData) => {
        const response = await apiRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify(userData)
        });

        if (response.success && response.data.token) {
            setToken(response.data.token);
        }

        return response;
    },

    /**
     * Login user
     */
    login: async (credentials) => {
        const response = await apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials)
        });

        if (response.success && response.data.token) {
            setToken(response.data.token);
        }

        return response;
    },

    /**
     * Get current user
     */
    getCurrentUser: async () => {
        return await apiRequest('/auth/me');
    },

    /**
     * Update user profile
     */
    updateProfile: async (profileData) => {
        return await apiRequest('/auth/profile', {
            method: 'PUT',
            body: JSON.stringify({ profile: profileData })
        });
    },

    /**
     * Logout user
     */
    logout: () => {
        removeToken();
        localStorage.removeItem('stem-quest-user');
        localStorage.removeItem('stemQuestUser');
        localStorage.removeItem('currentView');
        window.location.href = '/';
    },

    /**
     * Check if user is authenticated
     */
    isAuthenticated: () => {
        return !!getToken();
    }
};

/**
 * Quest API
 */
export const questAPI = {
    /**
     * Complete a quest
     */
    completeQuest: async (questId, completionData) => {
        return await apiRequest(`/quests/${questId}/complete`, {
            method: 'POST',
            body: JSON.stringify(completionData)
        });
    },

    /**
     * Get quest history
     */
    getHistory: async () => {
        return await apiRequest('/quests/history');
    },

    /**
     * Get quest stats
     */
    getStats: async (questId) => {
        return await apiRequest(`/quests/${questId}/stats`);
    },

    /**
     * Check if quest is completed
     */
    checkCompletion: async (questId) => {
        return await apiRequest(`/quests/${questId}/check`);
    }
};

/**
 * Leaderboard API
 */
export const leaderboardAPI = {
    /**
     * Get global leaderboard
     */
    getGlobal: async (limit = 50) => {
        return await apiRequest(`/leaderboard/global?limit=${limit}`);
    },

    /**
     * Get weekly leaderboard
     */
    getWeekly: async () => {
        return await apiRequest('/leaderboard/weekly');
    },

    /**
     * Get most improved leaderboard
     */
    getMostImproved: async () => {
        return await apiRequest('/leaderboard/improved');
    },

    /**
     * Get user rank
     */
    getUserRank: async () => {
        return await apiRequest('/leaderboard/rank');
    }
};

// Export helper functions
export { getToken, setToken, removeToken, API_URL };
